"use client"

import { useEffect, useState, useRef } from "react"
import { X, Upload, Paperclip, File, ImageIcon, Loader } from "lucide-react"
import { useDispatch, useSelector } from "react-redux"
import { updateLeadsActionManager } from "../../../../../services/action/manager"
import { uploadImageAction } from "../../../../../services/action/common"
import { Toaster } from "../../../../Toaster"

const ResultForm = ({disableField, close, data, onSuccess }) => {
  const [status, setStatus] = useState("")
  const [resultReason, setResultReason] = useState("")
  const [resultAttachment, setAttachments] = useState([])
  const [isDragging, setIsDragging] = useState(false)
  const { user } = useSelector((state) => state.authenticationReducer)
  const { isImageLoading } = useSelector((state) => state.commonReducer)
  const dispatch = useDispatch()
  const fileRef = useRef()
  const dropZoneRef = useRef()
  const [isSubmit,setIsSubmit] =useState(false)

  useEffect(() => {
    if (data) {
      setStatus(data?.result)
      setResultReason(data?.resultReason)
      // If data has resultAttachment or imageUrls, set them here
      if (data?.resultAttachment) {
        setAttachments(data.resultAttachment)
      }
    }
  }, [data])

  // Set up drag and drop event listeners
  useEffect(() => {
    const dropZone = dropZoneRef.current

    if (!dropZone) return

    const handleDragOver = (e) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(true)
    }

    const handleDragLeave = (e) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
    }

    const handleDrop = (e) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      const files = e.dataTransfer.files
      if (files && files.length > 0) {
        handleFiles(files)
      }
    }

    // Add event listeners
    dropZone.addEventListener("dragover", handleDragOver)
    dropZone.addEventListener("dragleave", handleDragLeave)
    dropZone.addEventListener("drop", handleDrop)

    // Clean up
    return () => {
      dropZone.removeEventListener("dragover", handleDragOver)
      dropZone.removeEventListener("dragleave", handleDragLeave)
      dropZone.removeEventListener("drop", handleDrop)
    }
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!status) {
      Toaster.error("Status is required!")
      return false
    }
    if (!resultReason) {
      Toaster.error("Reason is required!")
      return false
    }
    setIsSubmit(true)
    dispatch(
      updateLeadsActionManager({
        userType: user?.userType?.toLowerCase(),
        data: {
          result: status,
          _id: data?._id,
          resultReason,
          resultAttachment,
        },
        cb: ()=>{
          setIsSubmit(false)
          onSuccess()
        },
        errorCb:()=>{
          setIsSubmit(false)
        }
      }),
    )
  }

  const handleReset = () => {
    setStatus("")
    setResultReason("")
    setAttachments([])
  }

  const handleFiles = (files) => {
    const formData = new FormData()
    for (let i = 0; i < files.length; i++) {
      formData.append("images", files[i])
    }

    dispatch(
      uploadImageAction({
        data: formData,
        cb: (res) => {
            setAttachments((prev) => [...prev, ...res?.imageUrls])
        },
      }),
    )
  }

  const uploadImage = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files)
      e.target.value = null
    }
  }

  const removeAttachment = (index) => {
    // Remove from resultAttachment array
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }



  return (
    <div className="flex justify-center w-full p-4">
      <div className="w-full bg-white max-w-md rounded-lg shadow-lg p-6 border border-gray-200">
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Result Submission</h2>
          <button
            type="button"
            onClick={close || handleReset}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close form"
          >
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="mb-2">
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              id="status"
              disabled={disableField}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="" disabled>
                Select status
              </option>
              <option value="fail" className="text-rose-600">
                Fail
              </option>
              <option value="pass" className="text-emerald-600">
                Pass
              </option>
              <option value="dissatisfied" className="text-emerald-600">
                Dissatisfied
              </option>
            </select>
          </div>
          <div className="mb-4">
            <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
              Reason
            </label>
            <textarea
              id="reason"
              value={resultReason}
              disabled={disableField}
              onChange={(e) => setResultReason(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              rows={3}
            />
          </div>

          {/* Attachment Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Attachments</label>

            {/* File Upload Button with Drag & Drop */}
            <div
              ref={dropZoneRef}
              className={`flex items-center justify-center w-full ${
                isDragging ? "border-emerald-500 bg-emerald-50" : "border-gray-300 bg-gray-50 hover:bg-gray-100"
              } border-2 border-dashed rounded-lg cursor-pointer transition-colors h-32`}
              onClick={() => fileRef.current?.click()}
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                {isImageLoading ? (
                  <Loader className="w-8 h-8 mb-2 text-gray-500 animate-spin" />
                ) : (
                  <Upload className={`w-8 h-8 mb-2 ${isDragging ? "text-emerald-500" : "text-gray-500"}`} />
                )}
                <p className="mb-1 text-sm text-gray-700">
                  <span className="font-semibold">{isDragging ? "Drop files here" : "Click to upload"}</span>
                  {!isDragging && " or drag and drop"}
                </p>
                <p className="text-xs text-gray-500">PDF, Images, Documents (Max 10MB)</p>
              </div>
              <input
               disabled={disableField}
                ref={fileRef}
                type="file"
                className="hidden"
                multiple={true}
                onChange={uploadImage}
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              />
            </div>

            {/* File Preview */}
            {resultAttachment?.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Paperclip className="mr-1" size={16} />
                  Attached Files ({resultAttachment?.length})
                </h3>
                <ul className="space-y-2 max-h-48 overflow-y-auto pr-2">
                  {resultAttachment?.map((file, index) => (
                    <li
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-md border border-gray-200"
                    >
                      <div className="flex items-center overflow-hidden">
                        <div className="ml-2 overflow-hidden">
                          <p className="text-sm font-medium text-gray-700 truncate" title={file}>
                            <a href={file}>{file}</a>
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="ml-2 text-gray-400 hover:text-rose-500 transition-colors"
                        aria-label={`Remove ${file.name}`}
                      >
                        <X size={16} />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={!status || isImageLoading ||disableField || isSubmit}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2 px-4 rounded-md transition duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isImageLoading ? "Uploading..." :isSubmit?"Please wait...": "Submit Result"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ResultForm
